import ua from '@tencent/docs-user-agent';
import * as React from 'react';
import {
    Feature,
    TransitionService,
    WidgetService,
    TitlebarService,
    StateManager,
    ToolbarService,
    WorkbenchSettingsService,
    WorkbenchConfigurationState,
} from '@tencent/workbench';
import BaseToolbarFeature from '@framework/workbench/common/feature/base-toolbar-feature';
import { config, IMMERSIVE_TOGGLE } from './config';
import { ToolbarType } from '@src/js/constant';
import { ImmersiveToggleButton } from '@framework/workbench/ui/toggle/immersive-toggle-button';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWModuleType,
    TDWOperationType,
    TDWFileType,
    TDWActionType, EventCodeType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { FIXED_KEYS_DOUBLE, FIXED_KEYS_SINGLE } from '../../schema/constant';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { isLogin } from '@src/js/common/utils';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

const simplifyToolbarMode = getCacConfigByKey('simplifyToolbarMode', false);

export const TOOLBAR_MODE = 'toolbarMode';

const DELAY_TIME = 500;
/**
 * 切换/隐藏工具栏
 */
@Feature()
export class ToggleFeature extends BaseToolbarFeature {
    id = config.id;
    config = config;
    private toolbarMode;
    // 是否正在隐藏/显示动画中
    private isAnimation = false;
    private timer;

    public constructor(
        private readonly transitionService: TransitionService,
        private readonly widgetService: WidgetService,
        private readonly titlebarService: TitlebarService,
        private readonly stateManager: StateManager,
        private readonly toolbarService: ToolbarService,
        private readonly settingsService: WorkbenchSettingsService<'pc'>,
    ) {
        super();
        let toolbarMode = this.getAppService().viewableServiceLayer.toolbarStateManager.getToolbarMode();
        // 仅支持简约工具栏时手动设置工具栏状态
        if (simplifyToolbarMode && toolbarMode !== ToolbarType.SINGLE_TOOLBAR) {
            toolbarMode = ToolbarType.SINGLE_TOOLBAR;
            this.setToolbarMode(ToolbarType.SINGLE_TOOLBAR);
        }
        this.toolbarMode = toolbarMode;
        this.timer = null;
        this.injectImmersiveToggleButton();

        // 会议场景默认进入沉浸模式
        if (ua.isTencentMeeting) {
            this.hideMenu();
        }
    }

    public shouldHideToolbarMode() {
        return simplifyToolbarMode;
    }

    public getToolbarMode() {
        return this.toolbarMode;
    }

    /**
     * 设置工具栏模式
     * @param mode 单栏/双栏模式
     */
    public async setToolbarMode(mode) {
        // 获取当前模式
        const { toolbarMode } = this;
        const isShowMenu = toolbarMode === IMMERSIVE_TOGGLE;
        if (toolbarMode === mode) return;

        // 如果目前是沉浸模式，则先退出沉浸模式
        if (isShowMenu) {
            await this.showMenu();
        }

        // 更新schema，如果单双栏模式未变，则跳过更新schema
        const cacheMode = this.getAppService().viewableServiceLayer.toolbarStateManager.getToolbarMode();
        if (cacheMode !== mode) {
            switch (mode) {
                case ToolbarType.SINGLE_TOOLBAR:
                    this.settingsService.set('toolbar.fixedKeys', FIXED_KEYS_SINGLE);
                    break;
                case ToolbarType.DOUBLE_TOOLBAR:
                    this.settingsService.set('toolbar.fixedKeys', Object.values(FIXED_KEYS_DOUBLE));
                    break;
            }
            this.toolbarService.$forceUpdate();
        }

        this.toolbarMode = mode;

        // 通知后端并缓存工具栏模式，复用以前的逻辑
        this.getAppService().viewableServiceLayer.toolbarStateManager.setToolbarMode(mode);

        if (this.timer) {
            clearTimeout(this.timer);
        }
        this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarAniming = true;

        this.timer = setTimeout(() => {
            // 暂无法获取单双栏切换动画状态，把这500ms当做是正在播放动画
            this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarAniming = false;
        }, DELAY_TIME);

        // 上报
        this.tdwReport({
            module: TDWModuleType.TOOLBAR_MODE,
            action: TDWActionType.TOOLBAR_CHANGE,
            eventcode: EventCodeType.WORKBENCH_TOGGLE_OPEN_EVENT_CHANGE,
            ver1: this.toolbarMode,
        });
    }

    /**
     * 进入沉浸模式
     */
    public async hideMenu() {
        if (this.isAnimation) return;
        if (this.toolbarMode === IMMERSIVE_TOGGLE) return;
        this.isAnimation = true;
        this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarAniming = true;

        // 隐藏toolbar，隐藏titlebar
        await this.transitionService.apply([
            { name: 'titlebar', switchTo: false },
            { name: 'fixed-toolbar', switchTo: false },
        ]);

        // 触发画布的resize
        this.getAppService().viewableServiceLayer.toolbarStateManager.isHeaderFold = true;
        this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarAniming = false;
        // 展示浮动小箭头，展示title bar里的小箭头
        await this.transitionService.apply([
            { name: 'unfold', switchTo: false },
            { name: 'titlebar-unfold', switchTo: true },
        ]);
        this.toolbarMode = IMMERSIVE_TOGGLE;
        this.titlebarService.enterHover();
        this.isAnimation = false;

        this.tdwReport({
            module: TDWModuleType.TOOLBAR_MODE,
            action: TDWActionType.TOOLBAR_CHANGE,
            eventcode: EventCodeType.WORKBENCH_TOGGLE_OPEN_EVENT_CHANGE,
            ver1: this.toolbarMode,
        });
    }

    /**
     * 取消沉浸模式
     */
    public showMenu = async () => {
        if (this.isAnimation) return;
        this.isAnimation = true;
        this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarAniming = true;

        // 退出hover模式
        this.titlebarService.exitHover();

        // 根据title bar是否隐藏进入对应动画
        if (!this.titlebarService.isShowing()) {
            await this.exitWhenTitleBarHiding();
        } else {
            await this.exitWhenTitleBarShowing();
        }
        this.isAnimation = false;

        // 触发resize
        this.getAppService().viewableServiceLayer.toolbarStateManager.isHeaderFold = false;
        this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarAniming = false;
    };

    /**
     * 隐藏标题栏和工具栏
     */
    public hideHeader = () => {
        this.settingsService.set(WorkbenchConfigurationState.DisableTitlebar, true);
        this.settingsService.set(WorkbenchConfigurationState.DisableToolbar, true);
    };

    /**
     * 显示标题栏和工具栏
     */
    public showHeader = () => {
        this.settingsService.set(WorkbenchConfigurationState.DisableTitlebar, false);
        this.settingsService.set(WorkbenchConfigurationState.DisableToolbar, false);
    };

    /**
     * 打开面板上报
     */
    public tdwReport = (reportParam) => {
        const reportObj = {
            opername: TDWOperationType.DOC_WORD_EDITOR,
        };

        const fileType = 'file_type';
        reportObj[fileType] = TDWFileType.TOOLBAR_SLIDE;
        Object.assign(reportObj, reportParam);
        tdw(reportObj);
    };

    protected getCustomDisabledState(): boolean {
        return ENV.IS_PC_LOCAL_EDIT && !isLogin();
    }

    /**
     * 当title bar隐藏时，取消沉浸
     */
    private exitWhenTitleBarHiding = async () => {
        const cacheMode = this.getAppService().viewableServiceLayer.toolbarStateManager.getToolbarMode();
        this.toolbarMode = cacheMode;
        this.titlebarService.$forceUpdate();
        this.transitionService.apply([
            { name: 'unfold', switchTo: true },
        ]);
        this.transitionService.apply([
            { name: 'titlebar-unfold', switchTo: false },
        ]);
        await this.transitionService.apply([
            { name: 'fixed-toolbar', switchTo: true },
            { name: 'titlebar', switchTo: true, force: true },
        ]);
    };

    /**
     * 当title bar显示时，取消沉浸
     */
    private exitWhenTitleBarShowing = async () => {
        this.transitionService.apply([
            { name: 'titlebar-unfold', switchTo: false },
        ]);
        await this.transitionService.apply([
            { name: 'fixed-toolbar', switchTo: true },
            { name: 'titlebar', switchTo: true, force: true },
            { name: 'unfold', switchTo: true },
        ]);
        const cacheMode = this.getAppService().viewableServiceLayer.toolbarStateManager.getToolbarMode();
        this.toolbarMode = cacheMode;
        this.titlebarService.$forceUpdate();
    };


    /**
     * 注册动画
     * 通过transitionService.apply方法传入bool值来执行过渡动画
     * true时，exited -> entering -> entered
     * false时，entered -> exiting -> exited
     */
    private injectImmersiveToggleButton() {
        // 隐藏后，右上角的展开按钮动画
        this.transitionService.register('unfold', {
            duration: 350,
            entered: {
                top: -26,
                opacity: 0,
                transitionTimingFunction: 'cubic-bezier(0.42, 0, 0.58, 1), cubic-bezier(0.25, 0.1, 0.25, 1)',
                transitionProperty: 'top, opacity',
                transitionDuration: '0.35s, 0.35s',
            },
            entering: {
                top: 0,
                opacity: 1,
                transitionTimingFunction: 'cubic-bezier(0.42, 0, 0.58, 1), cubic-bezier(0.25, 0.1, 0.25, 1)',
                transitionProperty: 'top, opacity',
                transitionDuration: '0.35s, 0.35s',
            },
            exited: {
                top: 0,
                opacity: 1,
                transitionTimingFunction: 'cubic-bezier(0.42, 0, 0.58, 1)',
                transitionProperty: 'top, opacity',
                transitionDuration: '0.35s',
            },
            exiting: {
                top: -26,
                opacity: 0,
                transitionTimingFunction: 'cubic-bezier(0.42, 0, 0.58, 1)',
                transitionProperty: 'top, opacity',
                transitionDuration: '0.35s',
            },
        });

        // 注册展开图标
        this.widgetService.float({
            name: 'ImmersiveToggleButton',
            component: <ImmersiveToggleButton handleButtonClick={() => {
                this.showMenu();

                // 箭头上报，不同于一般的展开工具栏
                this.tdwReport({
                    module: TDWModuleType.PC_TOOLBAR,
                    action: TDWActionType.TOOLBAR_OPEN,
                    eventcode: EventCodeType.WORKBENCH_TOGGLE_OPEN,
                });
            }} />,
            layer: 'lower',
        });
    }
}
